import { SlideContent } from '../components/SlideEditor';
import { FileWithContent } from './PresentationService';
import { auth } from '../firebase';

export interface SlideOutline {
  slideNumber: number;
  slideType: string;
  title: string;
  content: string;
  notes?: string;
}

export interface PresentationOutline {
  slides: SlideOutline[];
  presentationTitle: string;
  presentationSummary: string;
}

export interface StreamingPlanCallback {
  onThinking?: (thinking: string) => void;
  onToolCall?: (toolCall: any) => void;
  onComplete?: (outline: PresentationOutline) => void;
  onError?: (error: string) => void;
}

// New interface for creating presentation outlines
export interface PresentationPrompt {
  topic: string;
  background: string;
  audience: string;
  presentationLength: string;
  format: string;
  contextFiles: FileWithContent[];
}

class SlidePlanService {
  private apiBaseUrl: string;

  constructor() {
    // Pages Functions API endpoints are relative to the domain root
    this.apiBaseUrl = '/api';
  }

  // Helper method to get auth token
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await auth.currentUser?.getIdToken();
    
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // New method for creating presentation outlines from topic, background, etc.
  async createPresentationOutline(
    prompt: PresentationPrompt,
    progressCallback?: StreamingPlanCallback
  ): Promise<PresentationOutline> {
    const request = {
      topic: prompt.topic,
      background: prompt.background,
      audience: prompt.audience,
      presentationLength: prompt.presentationLength,
      format: prompt.format,
      contextFiles: prompt.contextFiles
    };
    
    try {
      // Get authentication token for the POST request
      const headers = await this.getAuthHeaders();
      
      // Step 1: First make a POST request to submit data and get a clientId
      const postResponse = await fetch(`${this.apiBaseUrl}/presentation-outline`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });
      
      if (!postResponse.ok) {
        throw new Error(`API error: ${postResponse.status}`);
      }
      
      const { clientId } = await postResponse.json();
      
      if (!clientId) {
        throw new Error('No client ID received from server');
      }
      
      console.log('Received client ID for presentation outline:', clientId);
      
      // Get current auth token for the EventSource
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Step 2: Now create EventSource with the clientId parameter and auth token
      return new Promise((resolve, reject) => {
        const eventSource = new EventSource(
          `${this.apiBaseUrl}/presentation-outline?clientId=${clientId}&token=${encodeURIComponent(token)}`
        );
        
        // Track the thinking and tool calls
        let currentThinking = '';
        let presentationOutline: PresentationOutline | null = null;
        
        // Handle different event types
        eventSource.addEventListener('start', (event: Event) => {
          console.log('Presentation outline streaming started');
        });
        
        eventSource.addEventListener('content_block_start', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          
          if (data.content_block?.type === 'thinking') {
            console.log('Thinking started');
            currentThinking = '';
          }
        });
        
        eventSource.addEventListener('content_block_delta', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          
          if (data.delta?.type === 'thinking_delta') {
            currentThinking += data.delta.thinking;
            if (progressCallback?.onThinking) {
              progressCallback.onThinking(currentThinking);
            }
          }
        });
        
        eventSource.addEventListener('tool_call', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Tool call received:', data);
          
          if (progressCallback?.onToolCall) {
            progressCallback.onToolCall(data);
          }
          
          // Extract presentation outline from the tool call
          if (data.name === 'finalAnswer' && data.input) {
            try {
              presentationOutline = data.input as PresentationOutline;
            } catch (e) {
              console.error('Error parsing tool call input:', e);
            }
          }
        });
        
        eventSource.addEventListener('complete', (event: MessageEvent) => {
          console.log('Presentation outline streaming completed');
          
          eventSource.close();
          
          if (presentationOutline && progressCallback?.onComplete) {
            progressCallback.onComplete(presentationOutline);
          }
          
          if (presentationOutline) {
            resolve(presentationOutline);
          } else {
            reject(new Error('No presentation outline was generated'));
          }
        });
        
        eventSource.addEventListener('error', (event: MessageEvent) => {
          console.error('EventSource error:', event);
          
          let errorMessage = 'Unknown error occurred';
          try {
            const data = JSON.parse(event.data);
            errorMessage = data.error || errorMessage;
          } catch (e) {
            // Use generic error message if parsing fails
          }
          
          if (progressCallback?.onError) {
            progressCallback.onError(errorMessage);
          }
          
          eventSource.close();
          reject(new Error(errorMessage));
        });
        
        eventSource.onerror = (error) => {
          console.error('EventSource connection error:', error);
          eventSource.close();
          reject(new Error('Connection error occurred'));
        };
      });
    } catch (error) {
      console.error('Error in createPresentationOutline:', error);
      throw error;
    }
  }

  async generateSlidePlan(
    content: SlideContent, 
    contextFiles: FileWithContent[] = [],
    progressCallback?: StreamingPlanCallback
  ): Promise<PresentationOutline> {
    const request = {
      title: content.title,
      content: content.content,
      contextFiles
    };
    
    try {
      // Get authentication token for the POST request
      const headers = await this.getAuthHeaders();
      
      // Step 1: First make a POST request to submit data and get a clientId
      const postResponse = await fetch(`${this.apiBaseUrl}/slide-plan`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });
      
      if (!postResponse.ok) {
        throw new Error(`API error: ${postResponse.status}`);
      }
      
      const { clientId } = await postResponse.json();
      
      if (!clientId) {
        throw new Error('No client ID received from server');
      }
      
      console.log('Received client ID for slide plan:', clientId);
      
      // Get current auth token for the EventSource
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Step 2: Now create EventSource with the clientId parameter and auth token
      return new Promise((resolve, reject) => {
        const eventSource = new EventSource(
          `${this.apiBaseUrl}/slide-plan?clientId=${clientId}&token=${encodeURIComponent(token)}`
        );
        
        // Track the thinking and tool calls
        let currentThinking = '';
        let presentationOutline: PresentationOutline | null = null;
        
        // Handle different event types
        eventSource.addEventListener('start', (event: Event) => {
          console.log('Slide plan streaming started');
        });
        
        eventSource.addEventListener('message_start', (event: MessageEvent) => {
          console.log('Message started');
          if (progressCallback?.onThinking) {
            progressCallback.onThinking('Starting to analyze your content...');
          }
        });
        
        eventSource.addEventListener('content_block_start', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Content block start:', data);
          
          if (data.content_block?.type === 'thinking') {
            console.log('Thinking started');
            currentThinking = '';
          } else if (data.content_block?.type === 'tool_use') {
            console.log('Tool use started:', data.content_block);
            // Check if this is a create_slide_outline tool
            if (data.content_block?.name === 'create_slide_outline') {
              // Initialize an empty object to collect the tool input
              console.log('Found create_slide_outline tool block');
            }
          }
        });
        
        eventSource.addEventListener('content_block_delta', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          
          if (data.delta?.type === 'thinking_delta') {
            currentThinking += data.delta.thinking;
            if (progressCallback?.onThinking) {
              progressCallback.onThinking(currentThinking);
            }
          } else if (data.delta?.type === 'text_delta') {
            // Handle text deltas if needed
            console.log('Text delta received:', data.delta.text);
          } else if (data.delta?.type === 'input_json_delta') {
            // Handle tool input JSON deltas
            console.log('JSON delta received:', data.delta.partial_json);
          }
        });
        
        eventSource.addEventListener('content_block_stop', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Content block stopped:', data);
          
          // If this is the end of a tool_use block, we need to check if tool data is complete
          if (data.index !== undefined && data.index === 2) { // Tool use is typically the 3rd block (index 2)
            console.log('Tool use block complete, checking for presentation outline');
          }
        });
        
        eventSource.addEventListener('message_delta', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Message delta received:', data);
          
          // Check if this is a tool_use message
          if (data.delta?.stop_reason === 'tool_use') {
            console.log('Tool use message detected');
          }
        });
        
        eventSource.addEventListener('message_stop', (event: MessageEvent) => {
          console.log('Message stopped');
        });
        
        // This is specifically for the complete tool call, which comes separately
        eventSource.addEventListener('tool_call', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.log('Tool call received:', data);
          
          if (progressCallback?.onToolCall) {
            progressCallback.onToolCall(data);
          }
          
          // Extract presentation outline from the tool call
          if (data.name === 'create_slide_outline' && data.input) {
            try {
              presentationOutline = data.input as PresentationOutline;
              console.log('Successfully extracted presentation outline from tool call');
            } catch (e) {
              console.error('Error parsing tool call input:', e);
            }
          }
        });
        
        // We need to manually handle the tool use from the tool_use content block
        // This requires assembling JSON from partial deltas
        let toolJsonInput = '';
        
        eventSource.addEventListener('content_block_delta', (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            if (data.delta?.type === 'input_json_delta' && data.delta?.partial_json !== undefined) {
              toolJsonInput += data.delta.partial_json;
              
              // Try to parse the tool input if it looks complete
              if (toolJsonInput.trim().endsWith('}]}"') || toolJsonInput.trim().endsWith(']}')) {
                try {
                  // Clean up any potential artifacts and try to parse
                  const cleanedInput = toolJsonInput.replace(/\}\]\}"/g, '}]}');
                  const parsedInput = JSON.parse(cleanedInput);
                  
                  if (parsedInput.slides && Array.isArray(parsedInput.slides)) {
                    console.log('Successfully parsed presentation outline from JSON delta');
                    presentationOutline = parsedInput as PresentationOutline;
                    
                    if (progressCallback?.onToolCall) {
                      progressCallback.onToolCall({
                        name: 'create_slide_outline',
                        input: presentationOutline
                      });
                    }
                  }
                } catch (e) {
                  // Still accumulating JSON, not an error
                  console.log('Still building JSON input...');
                }
              }
            }
          } catch (e) {
            console.error('Error processing content_block_delta for JSON:', e);
          }
        });
        
        eventSource.addEventListener('complete', (event: MessageEvent) => {
          console.log('Slide plan streaming completed');
          
          eventSource.close();
          
          // As a fallback, try to parse the complete toolJsonInput if we haven't already
          if (!presentationOutline && toolJsonInput) {
            try {
              // Do our best to fix issues with the JSON
              let fixedJson = toolJsonInput
                .replace(/\}\]\}"/g, '}]}')
                .replace(/\"\{/g, '{')
                .replace(/\}\"/g, '}');
                
              // Find the position of the first { and last }
              const startPos = fixedJson.indexOf('{');
              const endPos = fixedJson.lastIndexOf('}') + 1;
              
              if (startPos >= 0 && endPos > startPos) {
                fixedJson = fixedJson.substring(startPos, endPos);
                
                // Try parsing the fixed JSON
                const parsedOutline = JSON.parse(fixedJson);
                
                if (parsedOutline.slides && Array.isArray(parsedOutline.slides)) {
                  console.log('Successfully parsed presentation outline from complete JSON input');
                  presentationOutline = parsedOutline as PresentationOutline;
                }
              }
            } catch (e) {
              console.error('Failed to parse complete JSON input:', e);
            }
          }
          
          if (presentationOutline && progressCallback?.onComplete) {
            progressCallback.onComplete(presentationOutline);
          }
          
          if (presentationOutline) {
            resolve(presentationOutline);
          } else {
            console.error('No presentation outline was generated. Accumulated JSON:', toolJsonInput);
            reject(new Error('No presentation outline was generated'));
          }
        });
        
        eventSource.addEventListener('error', (event: MessageEvent) => {
          console.error('EventSource error:', event);
          
          let errorMessage = 'Unknown error occurred';
          try {
            const data = JSON.parse(event.data);
            errorMessage = data.error || errorMessage;
          } catch (e) {
            // Use generic error message if parsing fails
          }
          
          if (progressCallback?.onError) {
            progressCallback.onError(errorMessage);
          }
          
          eventSource.close();
          reject(new Error(errorMessage));
        });
        
        eventSource.onerror = (error) => {
          console.error('EventSource connection error:', error);
          eventSource.close();
          reject(new Error('Connection error occurred'));
        };
      });
    } catch (error) {
      console.error('Error in generateSlidePlan:', error);
      throw error;
    }
  }
}

const slidePlanService = new SlidePlanService();
export default slidePlanService; 